import {
  BackgroundImage,
  MultiCheckbox,
  Container,
  FormProvider,
  TextField,
  Radiobox,
} from "../../components";
import { aboutUs as aboutUsAssets } from "../../assets";
import { useForm } from "react-hook-form";
import { Alert, Box, Button, Stack, Typography } from "@mui/material";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";

export const ScheduleNow = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentUrl = `https://www.bluesagepools.com${location.pathname}`;

  const [showThankyou, setShowThankyou] = useState(false);
  const [fallbackVisible, setFallbackVisible] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(false);
  const [mailtoLink, setMailtoLink] = useState("");
  const [emailDraftBody, setEmailDraftBody] = useState("");
  const [copyError, setCopyError] = useState("");

  const supportEmail = "info@BlueSagePools.com";

  const methods = useForm({
    resolver: yupResolver(
      Yup.object().shape({
        firstName: Yup.string().required("Required"),
        lastName: Yup.string().required("Required"),
        email: Yup.string().email().required("Required"),
        street: Yup.string().required("Required"),
        city: Yup.string().required("Required"),
        zipcode: Yup.number().required("Required"),
        phone: Yup.string()
          .matches(
            /^\+?\d{1,3}?[-.\s]?(\(?\d{1,4}?\)?[-.\s]?)?\d{2,9}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
            "Invalid Number"
          )
          .required("Required"),
        hearFrom: Yup.string().required("Required"),
        hasCurrentContract: Yup.bool().required("Required"),
      })
    ),
    defaultValues: { interest: [] },
    mode: "all",
  });

  const { handleSubmit } = methods;

  const buildEmailContent = (formValues) => {
    const {
      firstName,
      lastName,
      email,
      street,
      city,
      zipcode,
      phone,
      interest,
      hearFrom,
      hasCurrentContract,
      message,
    } = formValues;

    const subject = `New Schedule Request - ${firstName || ""} ${
      lastName || ""
    }`.trim();

    const body = `
New Pool Service Inquiry

First Name: ${firstName || ""}
Last Name: ${lastName || ""}
Email: ${email || ""}
Phone: ${phone || ""}

Street Address: ${street || ""}
City: ${city || ""}
Zip Code: ${zipcode || ""}

Interests: ${Array.isArray(interest) ? interest.join(", ") : ""}
How did you hear about us?: ${hearFrom || ""}
Current pool maintenance contract?: ${
      hasCurrentContract === true ? "Yes" : hasCurrentContract === false ? "No" : ""
    }

Message:
${message || ""}
`.trim();

    const mailto = `mailto:${supportEmail}?subject=${encodeURIComponent(
      subject
    )}&body=${encodeURIComponent(body)}`;

    return { subject, body, mailto };
  };

  const copyText = async (text, onSuccess) => {
    setCopyError("");
    try {
      if (navigator && navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(text);
        onSuccess();
        return;
      }

      const textArea = document.createElement("textarea");
      textArea.value = text;
      textArea.style.position = "fixed";
      textArea.style.left = "-9999px";
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) {
        onSuccess();
      } else {
        setCopyError("Could not copy automatically. Please copy it manually.");
      }
    } catch (err) {
      console.error("Clipboard copy failed", err);
      setCopyError("Could not copy automatically. Please copy it manually.");
    }
  };

  const onSubmitHandler = (formValues) => {
    console.log({ formValues });

    const { body, mailto } = buildEmailContent(formValues);

    setMailtoLink(mailto);
    setEmailDraftBody(body);
    setFallbackVisible(true);
    setCopiedEmail(false);
    setCopiedMessage(false);
    setCopyError("");

    // Try to open the user's default mail client
    window.location.href = mailto;

    // Always show fallback so user isn't stuck if no mail app opens
    setShowThankyou(true);
  };

  const handleEmailLinkClick = () => {
    window.location.href = `mailto:${supportEmail}`;
  };

  const thankYouNote = useMemo(
    () => (
      <>
        <Typography variant="h3" sx={{ my: 4 }} textAlign="center">
          Thank you!
        </Typography>
        <Typography variant="body1" textAlign="center">
          Email us at{" "}
          <Box
            component="button"
            type="button"
            onClick={handleEmailLinkClick}
            sx={{
              border: "none",
              background: "none",
              color: "primary.main",
              textDecoration: "underline",
              cursor: "pointer",
              p: 0,
              font: "inherit",
            }}
          >
            {supportEmail}
          </Box>
          .
        </Typography>
        <Typography variant="body1" textAlign="center">
          If your mail app did not open, use the options below.
        </Typography>
      </>
    ),
    []
  );

  return (
    <>
      <Helmet>
        <title>Schedule Pool Services | Blue Sage Pools</title>
        <meta
          name="description"
          content="Easily schedule pool services with Blue Sage Pools. Submit your details and let us help with pool maintenance, construction, renovation, and more."
        />
        <meta
          name="keywords"
          content="Schedule pool services, pool maintenance, pool construction, pool renovation, Blue Sage Pools, outdoor kitchen, landscape design"
        />
        <meta
          property="og:title"
          content="Schedule Pool Services | Blue Sage Pools"
        />
        <meta
          property="og:description"
          content="Easily schedule pool services with Blue Sage Pools. Submit your details and let us help with pool maintenance, construction, renovation, and more."
        />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:image" content={aboutUsAssets.blueSagePoolCover} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta
          name="twitter:title"
          content="Schedule Pool Services | Blue Sage Pools"
        />
        <meta
          name="twitter:description"
          content="Easily schedule pool services with Blue Sage Pools. Submit your details and let us help with pool maintenance, construction, renovation, and more."
        />
        <meta name="twitter:image" content={aboutUsAssets.blueSagePoolCover} />
        <link rel="canonical" href={currentUrl} />
      </Helmet>

      <BackgroundImage
        sx={{ height: "45vh" }}
        src={aboutUsAssets.blueSagePoolCover}
        alt="Blue Sage Pools"
      />

      <Container sx={{ my: { xs: 5, sm: 10, md: 15 } }}>
        <Box sx={{ maxWidth: "55rem", mx: "auto" }}>
          {showThankyou ? (
            <>
              {thankYouNote}

              {fallbackVisible && (
                <Box
                  sx={{
                    mt: 4,
                    p: 3,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    backgroundColor: "background.paper",
                  }}
                >
                  <Typography variant="h5" gutterBottom>
                    Didn’t see an email app open?
                  </Typography>

                  <Typography variant="body1" sx={{ mb: 2 }}>
                    Click the email address below or copy it and paste it into your
                    email app.
                  </Typography>

                  <Stack spacing={2}>
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Email Address
                      </Typography>
                      <Typography
                        component="a"
                        href={mailtoLink || `mailto:${supportEmail}`}
                        sx={{
                          color: "primary.main",
                          textDecoration: "underline",
                          wordBreak: "break-word",
                          display: "inline-block",
                          mb: 1,
                        }}
                      >
                        {supportEmail}
                      </Typography>

                      <Box>
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() =>
                            copyText(supportEmail, () => {
                              setCopiedEmail(true);
                              setTimeout(() => setCopiedEmail(false), 2500);
                            })
                          }
                        >
                          Copy Email Address
                        </Button>
                      </Box>

                      {copiedEmail && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Email address copied to clipboard.
                        </Alert>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                        Copy Full Message (includes your form answers)
                      </Typography>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() =>
                          copyText(emailDraftBody, () => {
                            setCopiedMessage(true);
                            setTimeout(() => setCopiedMessage(false), 2500);
                          })
                        }
                      >
                        Copy Message
                      </Button>

                      {copiedMessage && (
                        <Alert severity="success" sx={{ mt: 1 }}>
                          Message copied to clipboard. Open your email app, paste it,
                          and send to {supportEmail}.
                        </Alert>
                      )}
                    </Box>

                    {copyError && <Alert severity="warning">{copyError}</Alert>}
                  </Stack>

                  <Box sx={{ mt: 3 }}>
                    <Typography variant="subtitle2" sx={{ mb: 1 }}>
                      Message Preview
                    </Typography>
                    <Box
                      sx={{
                        whiteSpace: "pre-wrap",
                        p: 2,
                        border: "1px solid",
                        borderColor: "divider",
                        borderRadius: 1,
                        backgroundColor: "grey.50",
                        fontFamily: "monospace",
                        fontSize: "0.9rem",
                      }}
                    >
                      {emailDraftBody}
                    </Box>
                  </Box>
                </Box>
              )}

              <Box textAlign="center" sx={{ mt: 3 }}>
                <Button variant="contained" onClick={() => navigate("/")}>
                  Go back to Home page
                </Button>
              </Box>
            </>
          ) : (
            <>
              <Typography variant="h3" sx={{ my: 4 }} textAlign="center">
                How can we help you today
              </Typography>

              <Typography variant="body1" textAlign="center" sx={{ mb: 3 }}>
                Prefer email?{" "}
                <Box
                  component="button"
                  type="button"
                  onClick={handleEmailLinkClick}
                  sx={{
                    border: "none",
                    background: "none",
                    color: "primary.main",
                    textDecoration: "underline",
                    cursor: "pointer",
                    p: 0,
                    font: "inherit",
                  }}
                >
                  Email us at {supportEmail}
                </Box>
              </Typography>

              <FormProvider
                methods={methods}
                onSubmit={handleSubmit(onSubmitHandler)}
                sx={{ display: "flex", flexDirection: "column", gap: 4 }}
              >
                <Stack spacing={2}>
                  <TextField name="firstName" label="First Name" required />
                  <TextField name="lastName" label="Last Name" required />
                  <TextField name="email" label="Email" required />
                  <TextField name="street" label="Street Address" required />
                  <TextField name="city" label="City" required />
                  <TextField type="number" name="zipcode" label="Zip Code" required />
                  <TextField name="phone" label="Daytime phone" required />
                </Stack>

                <Box>
                  <Typography variant="h5" textAlign="center" gutterBottom>
                    What interest do you have?
                  </Typography>
                  <Box>
                    <MultiCheckbox
                      name="interest"
                      options={[
                        {
                          value: "pool_service_and_maintenance",
                          label: "Pool Service And Maintenance",
                        },
                        {
                          value: "new_pool_construction",
                          label: "New Pool Construction",
                        },
                        {
                          value: "pool_renovation",
                          label: "Pool Renovation",
                        },
                        {
                          value: "outdoor_kitchen",
                          label: "Outdoor Kitched",
                        },
                        {
                          value: "landscape_design",
                          label: "Landscape Design",
                        },
                      ]}
                    />
                  </Box>
                </Box>

                <TextField
                  name="hearFrom"
                  label="How Did You Hear About Us?"
                  required
                />

                <Radiobox
                  required
                  title="Do you currently have a pool maintenance contract?"
                  name="hasCurrentContract"
                  labels={[
                    { label: "No", value: false },
                    { label: "Yes", value: true },
                  ]}
                />

                <TextField name="message" label="Message" rows={10} multiline />

                <Box sx={{ textAlign: "center" }}>
                  <Button variant="contained" type="submit">
                    Submit
                  </Button>
                </Box>
              </FormProvider>
            </>
          )}
        </Box>
      </Container>
    </>
  );
};